<template>
  <div class="calendar">
    <!-- Header -->
    <div>
      <div class="flex items-center justify-center py-4 bg-base-100">
        <span class="text-sm font-normal uppercase text-base-500"> {{ $t('Select a date range') }}</span>
      </div>
      <div class="flex items-center py-4 border-b border-base-400 justify-evenly">
        <div class="px-4 py-1 text-sm font-normal capitalize border rounded-full border-base-700 month-input">
          {{ startDateText }}
        </div>
        <div class="px-4 py-1 text-sm font-normal capitalize border rounded-full border-base-700 month-input">
          {{ endDateText }}
        </div>
      </div>
    </div>
    <!-- Calendar -->
    <div class="vl-calendar">
      <span
        class="transition duration-200 text-base-700 hover:text-primary-500 vl-calendar__arrow vl-calendar__arrow--back"
        :class="{ 'disabled': disableMoveBack }"
        @click="moveBack()"
      >
        <ArrowLeftCircleIcon class="w-5 h-5" />
      </span>
      <span
        class="transition duration-200 text-base-700 hover:text-primary-500 vl-calendar__arrow vl-calendar__arrow--forward"
        :class="{ 'disabled': disableMoveForward }"
        @click="moveForward()"
      >
        <ArrowRightCircleIcon class="w-5 h-5" />
      </span>

      <div class="calendar--wrapper">
        <CalendarMonth
          :month="currentMonth"
          :year="currentYear"
          first-day-of-week="mo"
        >
          <template #header="{ monthName }">
            <div class="flex justify-center mb-5">
              <span> {{ `${monthName} ${currentYear}` }} </span>
            </div>
          </template>
          <template #days="{ day, getDate, firstOfMonth, lastOfMonth, firstOfWeek, lastOfWeek }">
            <BasicDay
              :is-selected="isSelected(getDate(day))"
              :is-first-selected="isFirstSelected(getDate(day))"
              :is-last-selected="isLastSelected(getDate(day))"
              :is-disabled="config.isDisabled(getDate(day))"
              :in-range="inRange(getDate(day))"
              :first-of-month="firstOfMonth(day)"
              :last-of-month="lastOfMonth(day)"
              :first-of-week="firstOfWeek(day)"
              :last-of-week="lastOfWeek(day)"
              @click="selectDate(getDate(day))"
              @mouseover="hoverEndDate(getDate(day))"
            >
              {{ day }}
            </BasicDay>
          </template>
        </CalendarMonth>

        <CalendarMonth
          :month="nextMonth"
          :year="nextYear"
          first-day-of-week="mo"
        >
          <template #header="{ monthName }">
            <div class="flex justify-center mb-5">
              <span> {{ `${monthName} ${nextYear}` }} </span>
            </div>
          </template>
          <template #days="{ day, getDate, firstOfMonth, lastOfMonth, firstOfWeek, lastOfWeek }">
            <BasicDay
              :is-selected="isSelected(getDate(day))"
              :is-first-selected="isFirstSelected(getDate(day))"
              :is-last-selected="isLastSelected(getDate(day))"
              :is-disabled="config.isDisabled(getDate(day))"
              :in-range="inRange(getDate(day))"
              :first-of-month="firstOfMonth(day)"
              :last-of-month="lastOfMonth(day)"
              :first-of-week="firstOfWeek(day)"
              :last-of-week="lastOfWeek(day)"
              @click="selectDate(getDate(day))"
              @mouseover="hoverEndDate(getDate(day))"
            >
              {{ day }}
            </BasicDay>
          </template>
        </CalendarMonth>
      </div>
    </div>
    <!-- Footer -->
    <div class="flex items-center justify-end px-6 py-4 space-x-4 border-t border-base-400">
      <LpButton
        data-testid="cancelButton"
        mode="transparent"
        color="gray"
        size="medium"
        @click="onCancelClick"
      >
        {{ $t('Cancel') }}
      </LpButton>
      <LpButton
        data-testid="selectDateButton"
        size="medium"
        :disabled="!startDate || !endDate"
        @click="onSelectDateRange(startDate, endDate)"
      >
        {{ $t('Select Date Range') }}
      </LpButton>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
} from 'vue';

import moment from 'moment';

import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@thehotelsnetwork/icons/vue/linear';

// Components
import CalendarMonth from './CalendarMonth.vue';
import BasicDay from './Days/BasicDay.vue';

import i18n from '@/config/i18n';

export default {
  components: {
    ArrowLeftCircleIcon,
    ArrowRightCircleIcon,
    CalendarMonth,
    BasicDay,
  },
  props: {
    dB: { type: Number },
    dE: { type: Number },
    minMonth: { type: Number },
    maxMonth: { type: Number },
    onCancelClick: { type: Function },
    onSelectDateRange: { type: Function },
    config: {
      type: Object,
      default: () => ({
        isDisabled: () => false,
      }),
    },
  },
  setup(props) {
    // Data
    const startDate = ref(props.dB);
    const endDate = ref(props.dE);
    const baseDate = ref('');
    const tmpEndDate = ref(null);

    // If we receive startDate & endDate,
    // set tmpEndDate equal to endDate so we can print the range between.
    if (startDate.value && endDate.value) {
      tmpEndDate.value = endDate.value;
    }

    // Base date will take the startDate value (in case it's set) or today's date otherwise.
    // It will be used to calculate the month / year where the calendar should start when created.
    baseDate.value = startDate.value || moment(props.maxMonth || props.minMonth).valueOf();

    // Once initialized, check if dateAlign config param is set.
    // If it's right, move months accordingly
    if (props.config.datesAlign === 'right') {
      // Update months
      const baseDateMonth = moment.unix(baseDate.value).month();
      const endDateMonth = moment.unix(endDate.value).month();
      const monthsToAdd = (endDateMonth - baseDateMonth) - 1;
      // Months to add:
      // > 0: 1 or more months between DB and DE.
      // = 0: No months between DB and DE. This will take no change.
      // < 0: DB and DE are on the same month. Substract one month so it's align to the right.
      if (monthsToAdd > 0) {
        baseDate.value = moment.unix(baseDate.value).add(monthsToAdd, 'months').unix();
      } else if (monthsToAdd < 0) {
        baseDate.value = moment.unix(baseDate.value).subtract(1, 'months').unix();
      }
    }

    const currentMonth = computed(() => moment.unix(baseDate.value).month());
    const currentYear = computed(() => moment.unix(baseDate.value).year());
    const nextMonth = computed(() => (currentMonth.value + 1) % 12);
    const nextYear = computed(
      () => (currentMonth.value === 11 ? currentYear.value + 1 : currentYear.value),
    );

    const startDateText = computed(
      () => (startDate.value ? moment.unix(startDate.value).format('MMMM DD YYYY') : i18n.global.t('Choose a date')),
    );
    const endDateText = computed(() => (endDate.value
      ? moment.unix(endDate.value).format('MMMM DD YYYY') : i18n.global.t('Choose a date')));

    const disableMoveBack = computed(() => {
      const prevMth = moment.unix(baseDate.value).subtract(1, 'months').startOf('month').unix();
      return prevMth < props.minMonth;
    });

    const disableMoveForward = computed(() => {
      const nextMth = moment.unix(baseDate.value).add(2, 'months').startOf('month').unix();
      return nextMth > props.maxMonth;
    });

    // Functions
    function inRange(date) {
      if (startDate.value && tmpEndDate.value) {
        return date > startDate.value && date < tmpEndDate.value;
      }
      return false;
    }

    // When startDate and endDate (or tmpEndDate, if any of them exist) are the same day
    function isSelected(date) {
      return moment.unix(startDate.value).isSame(moment.unix(date), 'day')
        && (!tmpEndDate.value || moment.unix(tmpEndDate.value).isSame(moment.unix(date), 'day'));
    }

    // When endDate (tmpEndDate) is at least one day in the future from startDate.
    function isFirstSelected(date) {
      return moment.unix(startDate.value).isSame(moment.unix(date), 'day')
        && moment.unix(tmpEndDate.value).diff(moment.unix(startDate.value), 'days') >= 1;
    }

    function isLastSelected(date) {
      return startDate.value < date
        && (
          moment.unix(endDate.value).isSame(moment.unix(date), 'day')
          || moment.unix(tmpEndDate.value).isSame(moment.unix(date), 'day')
        );
    }

    function hoverEndDate(date) {
      if (startDate.value && !endDate.value) {
        tmpEndDate.value = date;
      }
    }

    function selectDate(date) {
      if (startDate.value && !endDate.value && date >= startDate.value) {
        endDate.value = date;
      } else {
        startDate.value = date;

        if (endDate.value) {
          // Reset End Date
          endDate.value = '';
          tmpEndDate.value = '';
        }
      }
    }

    function moveBack() {
      if (props.minMonth) {
        const newPrevMonth = moment.unix(baseDate.value).subtract(1, 'months').startOf('month').unix();
        if (newPrevMonth >= props.minMonth) {
          baseDate.value = moment.unix(baseDate.value).subtract(1, 'months').unix();
        }
      } else {
        // Just proceed
        baseDate.value = moment.unix(baseDate.value).subtract(1, 'months').unix();
      }
    }

    function moveForward() {
      if (props.maxMonth) {
        const newNextMonth = moment.unix(baseDate.value).add(2, 'months').startOf('month').unix();
        if (newNextMonth <= props.maxMonth) {
          baseDate.value = moment.unix(baseDate.value).add(1, 'months').unix();
        }
      } else {
        // Just proceed
        baseDate.value = moment.unix(baseDate.value).add(1, 'months').unix();
      }
    }

    return {
      currentMonth,
      currentYear,
      nextMonth,
      nextYear,
      isSelected,
      isFirstSelected,
      isLastSelected,
      inRange,
      hoverEndDate,
      selectDate,
      moveBack,
      moveForward,
      startDate,
      endDate,
      tmpEndDate,
      startDateText,
      endDateText,
      disableMoveBack,
      disableMoveForward,
    };
  },
};
</script>
<style scoped>
.calendar {

  min-width: 640px;

  .month-input {
    width: 220px;
  }

  .back-arrow {
    left: 1rem;
  }

  .back-text {
    margin-top: 2px;
  }

  .vl-calendar {
    @apply text-base-900 text-sm;
    display: flex;
    justify-content: space-evenly;
    padding: 20px;
    position: relative;
    user-select: none;
    min-width: 40rem;
    min-height: 22.5rem;

    &__arrow {
      position: absolute;
      top: 20px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 75%;
      cursor: pointer;

      &--back {
        left: 3.5rem;
      }

      &--forward {
        right: 3.5rem;
      }

      &--back,
      &--forward {
        &.disabled {
          opacity: .2;
          pointer-events: none;
        }
      }
    }

    .calendar--wrapper {
      @apply grid;
      grid-template-columns: repeat(2, minmax(16rem, 1fr));
      gap: 2rem;
      max-width: 40rem;
    }
  }
}
</style>
